import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { C, EASE_OUT } from '../kit';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, title, children }) => {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const currentY = useRef(0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      setOffset(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleTouchStart = (e: React.TouchEvent) => {
    // Only allow drag if we are at the top of the content
    const target = e.target as HTMLElement;
    const contentArea = target.closest('.sheet-content');
    if (contentArea && contentArea.scrollTop > 0) return;
    
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    if (diff > 0) {
      setOffset(diff);
      e.cancelable && e.preventDefault(); // prevent pull-to-refresh
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (offset > 150) {
      onClose();
    } else {
      setOffset(0);
    }
  };

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <Sheet
        $offset={isDragging ? offset : 0}
        $transition={!isDragging}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div className="handle-area">
          <div className="handle" />
          {title && <h2 className="sheet-title">{title}</h2>}
        </div>
        <div className="sheet-content">
          {children}
        </div>
      </Sheet>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: rgba(14, 27, 51, 0.6);
  backdrop-filter: blur(4px);
  animation: fadeIn 300ms ${EASE_OUT};

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const Sheet = styled.div<{ $offset: number; $transition: boolean }>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 85dvh;
  background: ${C.paper};
  color: ${C.ink};
  border-radius: 32px 32px 0 0;
  border: 4px solid ${C.ink};
  border-bottom: 0;
  display: flex;
  flex-direction: column;
  transform: translateY(${(p) => p.$offset}px);
  ${(p) =>
    p.$transition &&
    css`
      transition: transform 300ms ${EASE_OUT};
      animation: slideUp 300ms ${EASE_OUT};
    `}

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }

  .handle-area {
    padding: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-bottom: 3px dashed rgba(14, 27, 51, 0.2);
  }

  .handle {
    width: 48px;
    height: 6px;
    background: ${C.ink};
    border-radius: 3px;
    opacity: 0.5;
    margin-bottom: 12px;
  }

  .sheet-title {
    margin: 0;
    font-family: 'GROBOLD', sans-serif;
    font-size: 24px;
    text-transform: uppercase;
  }

  .sheet-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px 20px 100px;
    -webkit-overflow-scrolling: touch;
  }
`;
